// List values: look-alikes before a new value is added (spec §3.6), and the order a list shows in.

import type { ListName, ROUTING_STATUS_LIST } from "../entities";
import type { ListValueRecord, WarehouseField } from "./types";
import { PE_CLEARED } from "./pe-order";

/** The line field each list fills. */
export const LIST_FIELD: Record<ListName | typeof ROUTING_STATUS_LIST, WarehouseField> = {
  "Work needed": "workNeeded",
  "Inventory notes": "inventoryNotes",
  Lead: "lead",
  "PE Order Status": "peStatus",
  "PE Order Assigned to": "peAssignedTo",
  "Routing status": "routingStatus",
};

export const FIELD_LIST: Partial<Record<WarehouseField, ListName>> = {
  workNeeded: "Work needed",
  inventoryNotes: "Inventory notes",
  lead: "Lead",
  peStatus: "PE Order Status",
  peAssignedTo: "PE Order Assigned to",
};

/**
 * Work Needed holds several values of its list (the team, 2026-10-28): stored joined by "; ", which
 * no value of the list may hold; one value alone is a list of one, so every value saved before stays
 * valid. Shown and said comma-separated; a filter keeps a line if ANY of its values matches.
 */
// PE Order Assigned To the same way: often two people ("OLVIA / JOCELYN" in the workbook).
export const MULTI_FIELDS: readonly WarehouseField[] = ["workNeeded", "peAssignedTo"];
export const isMulti = (field: WarehouseField | undefined) => !!field && MULTI_FIELDS.includes(field);
export const VALUE_SEPARATOR = "; ";
/** A field's values, each once, in the order picked. */
export const splitValues = (stored: string | null | undefined) => [
  ...new Set(
    String(stored ?? "")
      .split(";")
      .map((v) => v.trim())
      .filter(Boolean),
  ),
];
export const joinValues = (values: readonly string[]) => splitValues(values.join(";")).join(VALUE_SEPARATOR);
/** How a cell, an export and Ask AI say them: "CARTON LABEL, ZIP TIES". */
export const valuesText = (stored: string | null | undefined) => splitValues(stored).join(", ");
/** Why a new value can't join a list that holds several per line, or null. */
export const multiValueProblem = (field: WarehouseField | undefined, value: string) =>
  isMulti(field) && value.includes(";")
    ? `A ${field === "workNeeded" ? "Work Needed" : "PE Order Assigned To"} value can't have “;” — it separates the values.`
    : null;

/** A new value as it is stored: trimmed, single spaces, upper case. */
export const cleanListValue = (v: string) => v.trim().replace(/\s+/g, " ").toUpperCase();

const norm = (v: string) => v.toUpperCase().replace(/[^A-Z0-9]/g, "");

function levenshtein(a: string, b: string): number {
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[b.length]!;
}

/** `short` is an abbreviation of `long`: same first letter, its letters in order (CTN → CARTON). */
const abbreviates = (short: string, long: string) => {
  if (short.length >= long.length || short[0] !== long[0]) return false;
  let i = 0;
  for (const ch of long) if (ch === short[i]) i++;
  return i === short.length;
};

/** Word by word: each pair the same, a typo apart, or one abbreviating the other. */
function wordsAlike(a: string, b: string): boolean {
  const xs = a
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .filter(Boolean);
  const ys = b
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .filter(Boolean);
  if (xs.length !== ys.length || xs.length < 2) return false;
  return xs.every((x, i) => {
    const y = ys[i]!;
    return (
      x === y || abbreviates(x, y) || abbreviates(y, x) || (Math.min(x.length, y.length) > 3 && levenshtein(x, y) <= 1)
    );
  });
}

/**
 * Whether two values look alike: the same letters and digits, one inside the other, a typo away,
 * or the same words abbreviated (CTN LABEL ~ CARTON LABEL). A value of three characters or fewer
 * matches only itself — at that length every edit is a different word.
 */
export function looksAlike(a: string, b: string): boolean {
  const x = norm(a);
  const y = norm(b);
  if (!x || !y) return false;
  if (x === y) return true;
  if (Math.min(x.length, y.length) <= 3) return false;
  if (Math.min(x.length, y.length) > 4 && (x.includes(y) || y.includes(x))) return true;
  if (wordsAlike(a, b)) return true;
  return levenshtein(x, y) <= Math.max(2, Math.floor(Math.max(x.length, y.length) / 6));
}

/** The values of a list that look like `value`, retired ones too (they can be restored). */
export const lookAlikes = (value: string, values: readonly ListValueRecord[]) =>
  values.filter((v) => looksAlike(v.value, value));

/** The value of the list that `typed` already is, case and spaces ignored — active or retired. */
export function sameListValue(typed: string, values: readonly ListValueRecord[]): ListValueRecord | undefined {
  const key = (v: string) => cleanListValue(v).replace(/ /g, "");
  return key(typed) ? values.find((v) => key(v.value) === key(typed)) : undefined;
}

/** A list's values for the picker: not retired, matches of `query` (prefix first), most used first. */
export function rankListValues(
  values: readonly ListValueRecord[],
  usage: ReadonlyMap<string, number>,
  query: string,
): ListValueRecord[] {
  const q = query.trim().toUpperCase();
  return values
    .filter((v) => v.retired !== "Yes" && (!q || v.value.toUpperCase().includes(q)))
    .sort((a, b) => {
      const pa = q && a.value.toUpperCase().startsWith(q) ? 0 : 1;
      const pb = q && b.value.toUpperCase().startsWith(q) ? 0 : 1;
      return pa - pb || (usage.get(b.value) ?? 0) - (usage.get(a.value) ?? 0) || a.value.localeCompare(b.value);
    });
}

/** How many lines use each value of `field` (a line with several counts under each). */
export function listUsage(lines: readonly { fields: Record<WarehouseField, string> }[], field: WarehouseField) {
  const usage = new Map<string, number>();
  for (const l of lines) {
    const v = l.fields[field];
    // PE Order Status emptied on purpose is no value of its list.
    for (const one of isMulti(field) ? splitValues(v) : v && v !== PE_CLEARED ? [v] : [])
      usage.set(one, (usage.get(one) ?? 0) + 1);
  }
  return usage;
}

/** A field's value with `from` values replaced by `to` (a rename or a merge); unchanged, the same text. */
export function relabelValue(field: WarehouseField, stored: string, from: readonly string[], to: string): string {
  if (!isMulti(field)) return from.includes(stored) ? to : stored;
  const values = splitValues(stored);
  return values.some((v) => from.includes(v)) ? joinValues(values.map((v) => (from.includes(v) ? to : v))) : stored;
}

/** A value kept in its own case (a Routing status, "Need info"): trimmed, single spaces. */
export const cleanCarrier = (v: string) => v.trim().replace(/\s+/g, " ");
